'use client'; // TODO: Fix the individual blocks

import React from 'react';
import LazyLoad from 'react-lazyload';
import classNames from 'classnames';
import { MoneyBlock } from './MoneyBlock';
import { WordpressPost } from '@benzinga/content-manager';
import type { BlockProps } from '@benzinga/blocks';
import { findSpecificBlocks } from '@benzinga/blocks-utils';
import { ErrorBoundary } from '@benzinga/core-ui';
import styles from './MoneyBlocks.module.scss';

export interface MoneyBlocksProps {
  blocks?: any[];
  className?: string;
  campaigns?: any;
  post?: WordpressPost | null;
  insertBeforeBlock?: {
    blockName: string;
    position?: number;
    componentToInsert: React.ReactElement;
  };
  insertAfterBlocks?: {
    blockName: string;
    position?: number;
    componentToInsert: React.ReactElement;
  }[];
  lazyLoadEnabled?: boolean;
  disallowLazyLoadBlockList?: string[];
  lazyLoadStartIndex?: number;
  type?: 'default' | 'sidebar' | 'article-bottom' | 'article-in-content' | 'floating-widget';
}

export const MoneyBlocks: React.FC<MoneyBlocksProps> = ({
  blocks,
  campaigns,
  className,
  disallowLazyLoadBlockList,
  insertAfterBlocks,
  insertBeforeBlock,
  lazyLoadEnabled = false,
  lazyLoadStartIndex,
  post,
  type,
}) => {
  if (!Array.isArray(blocks)) {
    return null;
  }

  const insertComponent = (
    blocks: BlockProps[],
    blockName: string,
    position = 0,
    currentBlock: BlockProps,
    componentToInsert: React.ReactElement,
  ) => {
    if (currentBlock.blockName !== blockName) return null;
    const foundBlocks = findSpecificBlocks(blocks, blockName);
    const result = foundBlocks[position];
    if (result) {
      return componentToInsert;
    }
    return null;
  };

  const renderBlock = (block: BlockProps, i: number) => {
    return (
      <ErrorBoundary
        disableFallback={true}
        key={`money-blocks-wrapper-item-${block?.blockName}-${i}`}
        name={`money-block-${block?.blockName}`}
      >
        <>
          {insertBeforeBlock?.blockName &&
            insertComponent(
              blocks,
              insertBeforeBlock?.blockName,
              insertBeforeBlock?.position,
              block,
              insertBeforeBlock?.componentToInsert,
            )}

          <MoneyBlock block={block} campaigns={campaigns} post={post} type={type} />

          {Array.isArray(insertAfterBlocks) &&
            insertAfterBlocks.map(insertAfterBlock => (
              <React.Fragment key={`${insertAfterBlock.blockName}-${insertAfterBlock?.position}`}>
                {insertAfterBlock?.blockName
                  ? insertComponent(
                      blocks,
                      insertAfterBlock?.blockName,
                      insertAfterBlock?.position,
                      block,
                      insertAfterBlock?.componentToInsert,
                    )
                  : null}
              </React.Fragment>
            ))}
        </>
      </ErrorBoundary>
    );
  };

  const shouldLazyLoad = lazyLoadEnabled && typeof lazyLoadStartIndex === 'number';

  return (
    <div className={classNames(styles.moneyBlocksWrapper, 'money-blocks-wrapper', className)}>
      {blocks?.map((block: BlockProps, i: number) => {
        if (shouldLazyLoad && i > lazyLoadStartIndex && !disallowLazyLoadBlockList?.includes(block?.blockName)) {
          return (
            <LazyLoad height={150} key={`money-blocks-wrapper-lazy-${block?.blockName}-${i}`} offset={220} once>
              {renderBlock(block, i)}
            </LazyLoad>
          );
        } else {
          return renderBlock(block, i);
        }
      })}
    </div>
  );
};
